import { type TFunction } from 'i18next'
import React from 'react'
import * as R from 'remeda'

import {
  buildFiveSegmentsTableColumns as buildCustomerFiveSegmentsTableColumns,
  buildNineSegmentsTableColumns as buildCustomerNineSegmentsTableColumns,
  buildNineSegmentsTableInfo as buildCustomerNineSegmentsTableInfo,
  buildFiveSegmentsBrandsData,
  buildFiveSegmentsTableInfo,
  buildNineSegmentsBrandsData,
} from '~/apps/ninesegsAnalyzer/utils/customerAnalysis'
import {
  appendFiveSegDriverTop5Block,
  appendFiveSegSegmentTop5Block,
  appendNineSegDriverTop5Block,
  appendNineSegSegmentTop5Block,
  appendSampleThresholdBlock,
  buildFiveSegmentsAnswersArrayData,
  buildFiveSegmentsCustomerAttributesArrayData,
  buildFiveSegmentsData,
  buildFiveSegmentsTotalData,
  buildNineSegmentsAnswersArrayData,
  buildNineSegmentsCustomerAttributesArrayData,
  buildNineSegmentsData,
  buildNineSegmentsTotalData,
  buildFiveSegmentsTableColumns as buildStdFiveSegmentsTableColumns,
  buildFiveSegmentsTableInfo as buildStdFiveSegmentsTableInfo,
  buildNineSegmentsTableColumns as buildStdNineSegmentsTableColumns,
  buildNineSegmentsTableInfo as buildStdNineSegmentsTableInfo,
  type CountAnswer,
  fiveSegmentThresholdInputs,
  nineSegmentThresholdInputs,
} from '~/apps/ninesegsAnalyzer/utils/standardAnalysis'
// Question/answer-option cell wrap widths are borrowed as-is from the
// screen-side CSS modules. The column track is max-content, so without a
// max-width the text stretches onto one line and the saved image renders at
// a different width than the screen. Duplicating the values here would leave
// the saved image behind whenever the screen-side styles change.
import matrixTableStyles from '~/components/features/GroupCountsFigures/components/MatrixTable.css'
import sequentialTableStyles from '~/components/features/GroupCountsFigures/components/SequentialTable.css'
import {
  buildMatrixAnswersData,
  buildMatrixSubQuestions,
  buildMatrixTableColumns,
  buildMatrixTableInfo,
  buildNormalAnswersArrayData,
  buildSequentialTableColumns,
  buildSequentialTableInfo,
  type CountAnswers,
  type GroupsDiffColumnKey,
  type QuestionsDiffColumnKey,
} from '~/components/features/GroupCountsFigures/utils'
import { Table } from '~/components/features/Table'
import { type Brand, type Question } from '~/hooks/useSurveyQuery'
import {
  exists,
  type Group,
  groupAllLabelI18nKey,
  type GroupAndAll,
  type NineSegment,
  QuestionOp,
  serializeTableToText,
  toNineNumber,
} from '~/utils'

import { type BasicOutputsResult } from './useGenerateBasicOutputs'

// The discriminated-union output member (narrowed by `__typename`). Each typed
// `result` below is the matching member's selected `result`, sourced from the
// status query's codegen — no hand-written engine-result types.
type BasicOutput =
  BasicOutputsResult['filterResults'][number]['brandResults'][number]['outputs'][number]
type Brands9segsResult = Extract<
  BasicOutput,
  { __typename?: 'Brands9segsInBrandGroupOutputResult' }
>['result']
type AnswersByMultiResult = Extract<
  BasicOutput,
  { __typename?: 'AnswersByMultiQuestionsOutputResult' }
>['result']
type CustomizedResult = Extract<
  BasicOutput,
  { __typename?: 'CustomizedAnalysisOutputResult' }
>['result']
type CustomerAttributeAxisResult = Extract<
  BasicOutput,
  { __typename?: 'CustomerAttributeAxisOutputResult' }
>['result']['customerAttributes']

/**
 * For each brand index, compute which question IDs are allowed:
 * - Normal questions: allowed for ALL brands (unchanged).
 * - Matrix questions:
 *   - brandAligned=true AND mt_questions.length === brandCount
 *     → only mt_questions[brandIndex] (1:1 diagonal, N items total).
 *   - Otherwise (brandAligned=false, OR count mismatch between sub-questions
 *     and brands) → ALL mt_questions for every brand (full N×M combinations).
 *
 * Count-mismatch handling: when mt_questions.length !== brandCount, the
 * diagonal mapping is ambiguous (which sub-question pairs with which brand?),
 * so we render the full N×M matrix for every brand. This keeps the output
 * complete and symmetric regardless of which side is larger.
 *
 * The engine always returns N×M tabulation data; this helper is purely a
 * client-side render filter.
 *
 * @param selectedQuestionIds question IDs the user picked for this output slot
 * @param questions all questions in the survey (for matrix lookup)
 * @param brandIndex zero-based index into the brand group's full brand list
 * @param brandCount total number of brands in the brand group (full list)
 * @param brandAligned true = diagonal 1:1 mapping; false = full N×M combinations
 * @returns flattened question IDs to render for the given brand
 */
export const buildAllowedQuestionIdsForBrand = (
  selectedQuestionIds: string[],
  questions: {
    mt_questions: string[]
    question_id: string
    question_text: string
  }[],
  brandIndex: number,
  brandCount: number,
  brandAligned: boolean,
): string[] => {
  const allowed: string[] = []
  for (const qId of selectedQuestionIds) {
    const q = questions.find((question) => question.question_id === qId)
    if (q && 0 < q.mt_questions.length) {
      if (brandAligned && q.mt_questions.length === brandCount) {
        allowed.push(q.mt_questions[brandIndex])
      } else {
        allowed.push(...q.mt_questions)
      }
    } else {
      allowed.push(qId)
    }
  }

  return allowed
}

// Resolve the per-(brand,segment) index into groupCounts/groupAnswers WITHOUT
// depending on answer rows. The analyzer resolver builds its group order
// server-side as `filteredBrands.flatMap(brand => [1..9].map(segNum => ...))`
// (buildGroupFiltersForBrands), and groupCounts/groupAnswers come back PARALLEL
// to it. So a brand at position k in the selected-brand list (same source +
// filter as selectedBrandsInOrder) owns the contiguous index block
// k*9 .. k*9+8, one slot per nine-segment (segNum 1..9).
//
// Reconstructing the index arithmetically (rather than locating the label in a
// sample answer row) is what mirrors StandardAnalysisPage: its index source
// (request-side groupFilters) is independent of how many answer rows returned.
// This survives the zero-respondent filter case where every answer row is
// empty — the old `countAnswers[0].groupAnswers` template was then [], so every
// lookup returned -1 and the (brand,segment) total cells rendered NaN.
const buildBrandNineSegmentIndexResolver =
  (selectedBrandsInOrder: { id: number }[]) =>
  (brand: Brand, nineSegment: NineSegment): number => {
    const brandPos = selectedBrandsInOrder.findIndex((x) => x.id === brand.id)
    if (brandPos < 0) {
      return -1
    }

    return brandPos * 9 + (toNineNumber(nineSegment) - 1)
  }

// ---- Public builders ----

export type TableElement = {
  element: React.ReactElement
  segmentMode: '5segs' | '9segs'
  textContent: string
}

export const buildCustomerAnalysisTableElements = (
  result: Brands9segsResult,
  sampleSize: number,
  t: TFunction,
): TableElement[] => {
  // codegen models `result` as nullable (the engine may omit the brand list,
  // e.g. a filter pattern that matches zero respondents). Mirror
  // CustomerAnalysisPage (useNineSegmentsBrandsData: `result ?? []`): default a
  // null brand list to [] and render an empty table rather than aborting the
  // whole generation. buildNineSegmentsBrandsData maps/sorts the array, so []
  // yields an empty (header-only) table with no throw and no NaN.
  const brands9segs = result.result ?? []

  // Shared pure functions own all math. basic-output is a structural adapter:
  // pass the brand list through unchanged (shape matches what
  // tabulationQueryDataAtom.result holds on the existing page).
  const brandsNineSegData = buildNineSegmentsBrandsData({
    brands9segs,
    sampleSize,
    sortValue: undefined,
  })
  const brandsFiveSegData = buildFiveSegmentsBrandsData({
    nineSegmentsBrandsData: brandsNineSegData,
    sampleSize,
    sortValue: undefined,
  })

  const nineSegColumns = buildCustomerNineSegmentsTableColumns({
    hiddenColumnKeys: [],
  })
  const nineSegTableInfo = buildCustomerNineSegmentsTableInfo({
    addHighlightProps: () => ({}),
    brandsData: brandsNineSegData,
    dataDisplayType: 'percentage',
    hiddenColumnKeys: [],
    hiddenRowIds: [],
    indicatorPadClassName: '',
    sortValue: null,
    t,
    tamRatio: 1,
  })

  const fiveSegColumns = buildCustomerFiveSegmentsTableColumns({
    hiddenColumnKeys: [],
  })
  const fiveSegTableInfo = buildFiveSegmentsTableInfo({
    addHighlightProps: () => ({}),
    brandsData: brandsFiveSegData,
    dataDisplayType: 'percentage',
    hiddenColumnKeys: [],
    hiddenRowIds: [],
    indicatorPadClassName: '',
    sortValue: null,
    t,
    tamRatio: 1,
  })

  return [
    {
      element: (
        <div style={{ backgroundColor: 'white', padding: 16 }}>
          <Table columns={fiveSegColumns} tableInfo={fiveSegTableInfo} />
        </div>
      ),
      segmentMode: '5segs',
      textContent: serializeTableToText(fiveSegColumns, fiveSegTableInfo),
    },
    {
      element: (
        <div style={{ backgroundColor: 'white', padding: 16 }}>
          <Table columns={nineSegColumns} tableInfo={nineSegTableInfo} />
        </div>
      ),
      segmentMode: '9segs',
      textContent: serializeTableToText(nineSegColumns, nineSegTableInfo),
    },
  ]
}

// Whether a matrix table or a 連続設問 table is drawn is decided by whether
// the SELECTED question itself owns sub-questions. This mirrors
// useGroupedQuestions on the page, which partitions the selection by its own
// mt_questions, so this function follows the same rule.
//
// Do NOT reverse-lookup the parent from the engine result's questionId.
// Selecting a matrix sub-question on its own would then have the page
// classify it as 連続設問 while this function classifies it as matrix, so the
// saved image renders in a different layout than the screen. The saved
// figure's figureType and the restore-link hash (useRenderAndSaveDocumentImages)
// are kept in step with this function's classification too.
export const findSelectedMatrixParents = ({
  questions,
  selectedQuestionIds,
}: {
  questions: ReadonlyArray<Question>
  selectedQuestionIds: ReadonlyArray<string>
}): Question[] =>
  questions.filter(
    (question) =>
      0 < question.mt_questions.length &&
      selectedQuestionIds.includes(question.question_id),
  )

export const buildCustomizedAnalysisTableElements = ({
  questions,
  result,
  sampleSize,
  selectedQuestionIds,
  t,
}: {
  questions: ReadonlyArray<Question>
  result: CustomizedResult
  sampleSize: number
  // The raw wizard selection assigned to this role, before the sub-question
  // expansion applied ahead of the engine request. The sole input to the
  // matrix classification.
  selectedQuestionIds: ReadonlyArray<string>
  t: TFunction
}): TableElement[] => {
  // codegen models `answers` and `answers.results` as nullable; null means the
  // engine returned no distribution data for this customized output, which is a
  // bug — fail fast rather than rendering nothing silently.
  if (!exists(result.answers) || !exists(result.answers.results)) {
    throw new Error(
      'basic-output: customized output has no answers (engine returned null distribution data)',
    )
  }

  // Slot type matches the shared cores' `(CountAnswers[] | null)[]`: each
  // non-null slot is a CountAnswersByQuestionResult[] (structurally CountAnswers).
  const results: (CountAnswers[] | null)[] = result.answers.results

  // Result slots are used ONLY to learn which questions this payload
  // contains — never fold them into the matrix vs 連続設問 classification
  // (see findSelectedMatrixParents).
  const resultQuestionIds = results
    .flat()
    .filter(exists)
    .map((result) => result.questionId)

  // All-null result slots are LEGITIMATE: a filter pattern that matches zero
  // respondents yields every slot null. Both layouts handle it gracefully
  // (individual: resultSlots.filter(exists) → empty; matrix:
  // buildMatrixAnswersData's `countAnswersArray[0]?.answers ?? []`) and render a
  // header-only table, so do NOT throw for an empty result — that would abort
  // the whole generation for a valid empty-result case.
  const matrixParents = findSelectedMatrixParents({
    questions,
    selectedQuestionIds,
  })

  if (1 < matrixParents.length) {
    throw new Error(
      'basic-output: a customized output spans multiple matrix parents, which is not supported (one question per customized role)',
    )
  }

  if (0 < matrixParents.length) {
    // One customized output carries ONE question/parent type (per the team's
    // reachability analysis), so a mix of matrix + ordinary questions is not
    // expected. Returning the matrix layout alone would silently drop any
    // ordinary questions in the same payload, so fail fast instead.
    const matrixSubIds = new Set(
      matrixParents.flatMap((parent) => parent.mt_questions),
    )
    const hasNonMatrix = resultQuestionIds.some((id) => !matrixSubIds.has(id))
    if (hasNonMatrix) {
      throw new Error(
        'basic-output: a customized output mixes matrix and ordinary questions, which is not supported (expected one question type per output)',
      )
    }

    return buildCustomizedMatrixLayout(
      results,
      questions,
      matrixParents,
      sampleSize,
      t,
    )
  }

  return buildCustomizedIndividualLayout(results, questions, sampleSize, t)
}

const buildCustomizedIndividualLayout = (
  resultSlots: (CountAnswers[] | null)[],
  questions: ReadonlyArray<Question>,
  sampleSize: number,
  t: TFunction,
): TableElement[] => {
  // sampleSize is the true respondent count (matches useTotalData on the
  // existing page: `surveyFilter?.count ?? survey.numberOfTotalAnswers`).
  // Do NOT derive from sumBy(answers): for MA questions sum > respondents,
  // which inflates the denominator and collapses all ratios toward 0%.

  // One inner array per question slot. Each inner array's first result carries
  // the question id; the shared core hydrates the survey question (so the cell
  // uses QuestionOp.toText `[id] question_text`) and shapes counts/ratios,
  // matching CustomizedAnalysisPage exactly.
  // A null slot = a question the engine returned no data for; it contributes no
  // table rows. Drop null slots FIRST so questionIds and results stay parallel.
  const slots = resultSlots.filter(exists)
  const questionIds = slots.map((questionResults) => {
    if (questionResults.length === 0) {
      throw new Error(
        'basic-output: empty result slot in customized individual output; engine must emit at least one result per requested question',
      )
    }

    return questionResults[0].questionId
  })
  const results: (CountAnswers[] | null)[] = slots

  const visibleAnswersArrayData = buildNormalAnswersArrayData({
    mapGroup: <V,>(fn: (group: GroupAndAll, index: number) => V) =>
      ({ all: fn('all', 0) }) as Record<GroupAndAll, V>,
    mapGroupsDiff: <V,>() => ({}) as Record<GroupsDiffColumnKey, V>,
    questionIds,
    questions: questions as Question[],
    results,
    totalCounts: { all: sampleSize } as Record<GroupAndAll, number>,
  })

  const tableInfo = buildSequentialTableInfo({
    // Unreachable for basic-output: no cell-highlight UI in offline render.
    addHighlightProps: () => ({}),
    customerAttributesRowGetters: {
      // Unreachable for basic-output: visibleCustomerAttributesArrayData is [].
      getAnswerText: () => '',
      getQuestionText: () => '',
      getRowId: () => '',
    },
    dataDisplayType: 'percentage',
    // Empty because groupsDiffs is []; mapGroupsDiff produces no entries so
    // diffMinMaxMap is never read. Record<K, V> is exhaustive; cast to satisfy.
    diffMinMaxMap: {} as Record<
      GroupsDiffColumnKey,
      { max: number; min: number }
    >,
    // Unreachable for basic-output: no sort UI in offline render. Returning
    // 'blank' matches getSortIconBySortValue's default (no active sort).
    getSortIcon: () => 'blank',
    groupAndAllNameMap: { all: t(groupAllLabelI18nKey) } as Record<
      GroupAndAll,
      string
    >,
    groupsDiffs: [],
    mapGroup: <V,>(fn: (group: GroupAndAll, index: number) => V) =>
      ({ all: fn('all', 0) }) as Record<GroupAndAll, V>,
    mapGroupsDiff: <V,>() => ({}) as Record<GroupsDiffColumnKey, V>,
    normalAnswersRowGetters: {
      getAnswerText: (rowData) => rowData.answerOption.text,
      getQuestionText: (rowData) => QuestionOp.toText(rowData.question),
      getRowId: (rowData) =>
        `${rowData.question.question_id}-${rowData.answerOption.option}`,
    },
    // Unreachable for basic-output: no group-name editing in offline render.
    setNameEditingGroup: () => {},
    // Identity: no UI-driven sort in offline render.
    sortFunc: (rowsData) => rowsData,
    styles: {
      answerText: sequentialTableStyles.cellAnswerTextColumn,
      questionText: sequentialTableStyles.cellQuestionTextColumn,
    },
    t,
    tamRatio: 1,
    totalData: {
      counts: { all: sampleSize } as Record<GroupAndAll, number>,
      ratios: { all: sampleSize === 0 ? 0 : 1 } as Record<GroupAndAll, number>,
    },
    visibleAnswersArrayData,
    visibleCustomerAttributesArrayData: [],
  })

  const columns = buildSequentialTableColumns({
    groupsDiffs: [],
    tabulatedGroupsAndAll: ['all'],
  })

  return [
    {
      element: (
        <div style={{ backgroundColor: 'white', padding: 16 }}>
          <Table columns={columns} tableInfo={tableInfo} />
        </div>
      ),
      segmentMode: '9segs',
      textContent: serializeTableToText(columns, tableInfo),
    },
  ]
}

const buildCustomizedMatrixLayout = (
  resultSlots: (CountAnswers[] | null)[],
  questions: ReadonlyArray<Question>,
  matrixParents: ReadonlyArray<Question>,
  sampleSize: number,
  t: TFunction,
): TableElement[] => {
  // One table per real matrix parent. Each parent's sub-question CountAnswers
  // come from the flattened result list, kept in ENGINE tabulation order (the
  // same order the existing-page matrix derives its rows from).
  // Drop null slots (questions the engine returned no data for) while keeping
  // ENGINE tabulation order for the remaining entries.
  const flatResults = resultSlots.flat().filter(exists)

  // Only the sub-questions actually tabulated for this output appear in the
  // results. Render exactly those: the wizard tabulates the selected
  // sub-question(s), so rendering the parent's full sub-question set would add
  // empty columns for unselected siblings.
  const resultQuestionIdSet = new Set(
    flatResults.map((result) => result.questionId),
  )

  return matrixParents.flatMap((parent): TableElement[] => {
    const subQuestions = buildMatrixSubQuestions(
      questions as Question[],
      parent,
    ).filter((subQuestion) => resultQuestionIdSet.has(subQuestion.question_id))
    const subQuestionIds = subQuestions.map(
      (subQuestion) => subQuestion.question_id,
    )

    // Gather this parent's sub-question count-answers by FILTERING the engine
    // result list (not re-mapping by survey ids), preserving ENGINE order. The
    // shared core (buildMatrixAnswersData) derives matrix ROWS from the FIRST
    // element's answers, and the interactive page (useMatrixAnswersData) feeds
    // results in engine order; mapping by survey order would diverge whenever
    // engine order ≠ survey order. Rows therefore follow the engine's first
    // sub-question, matching the page.
    const subQuestionIdSet = new Set(subQuestionIds)
    const subQuestionCountAnswers = flatResults.filter((result) =>
      subQuestionIdSet.has(result.questionId),
    )

    // sampleSize received from caller (the true respondent count). Do NOT derive
    // from answersData.groupCounts: basic-output sends no group filters, so
    // groupCounts is empty and a `: 0` fallback would render 0% everywhere.
    //
    // Rows come from the first sub-question's answer options only, matching the
    // existing-page matrix (buildMatrixAnswersData reads countAnswersArray[0]).
    const visibleAnswersData = buildMatrixAnswersData({
      group: 'all',
      mapQuestionsDiff: <V,>() => ({}) as Record<QuestionsDiffColumnKey, V>,
      questionId: parent.question_id,
      questions: [parent.question_id],
      results: [subQuestionCountAnswers],
      totalCount: sampleSize,
    })

    // mapSubQuestions header/column helper; resolves to the same per-sub-question
    // record useSubQuestions feeds the existing-page matrix.
    const mapSubQuestions = <V,>(
      fn: (subQuestion: Question) => V,
    ): Record<string, V> => {
      const out: Record<string, V> = {}
      for (const subQuestion of subQuestions) {
        out[subQuestion.question_id] = fn(subQuestion)
      }

      return out
    }

    const tableInfo = buildMatrixTableInfo({
      // Unreachable for basic-output: no cell-highlight UI in offline render.
      addHighlightProps: () => ({}),
      dataDisplayType: 'percentage',
      // Empty because questionsDiffs is []; mapQuestionsDiff produces no entries
      // so diffMinMaxMap is never read.
      diffMinMaxMap: {} as Record<
        QuestionsDiffColumnKey,
        { max: number; min: number }
      >,
      // Unreachable for basic-output: no sort UI in offline render.
      getSortIcon: () => 'blank',
      group: 'all',
      // Empty: 'all' branch is taken so groupNameMap is never read.
      groupNameMap: {} as Record<Group, string>,
      mapQuestionsDiff: <V,>() => ({}) as Record<QuestionsDiffColumnKey, V>,
      mapSubQuestions,
      // Unreachable for basic-output: no group-name editing in offline render.
      setNameEditingGroup: () => {},
      // Identity: no UI-driven sort in offline render.
      sortFunc: (rowsData) => rowsData,
      styles: {
        answerText: matrixTableStyles.cellAnswerTextColumn,
        answerTextHeader: matrixTableStyles.cellAnswerTextHeaderColumn,
      },
      t,
      tamRatio: 1,
      totalData: {
        counts: { all: sampleSize } as Record<GroupAndAll, number>,
        ratios: {
          all: sampleSize === 0 ? 0 : 1,
        } as Record<GroupAndAll, number>,
      },
      visibleAnswersData,
    })

    const columns = buildMatrixTableColumns({
      hiddenColumnKeys: [],
      question: parent,
      questionsDiffsMap: {},
    })

    return [
      {
        element: (
          <div style={{ backgroundColor: 'white', padding: 16 }}>
            <Table columns={columns} tableInfo={tableInfo} />
          </div>
        ),
        segmentMode: '9segs',
        textContent: serializeTableToText(columns, tableInfo),
      },
    ]
  })
}

export const buildQuestionTableElements = (
  result: AnswersByMultiResult,
  brandId: number,
  sampleSize: number,
  t: TFunction,
  allowedQuestionIds: string[] | undefined,
  questions: { question_id: string; question_text: string }[],
  // The selected brands in the SAME order the analyzer resolver builds its group
  // filters (brand-group brands filtered by selected ids — see planRenderTasks
  // `targetBrands`, which mirrors the resolver's `brands.filter(...)`). Used to
  // reconstruct the (brand,segment)→index mapping WITHOUT depending on answer
  // rows; see getBrandNineSegmentIndex below.
  selectedBrandsInOrder: { id: number }[],
  // ドライバーTop5ブロックのゲート (assembleStandardAnalysisTableElements 参照)。
  shouldAttachDriverBlock: (questionText: string) => boolean,
): TableElement[] => {
  // STRUCTURAL RESHAPE ONLY: flatten the engine's outer/inner result array
  // ([question[]]) to a flat CountAnswer list — the same shape the existing
  // page produces via `tabulationQueryData?.question?.results.flatMap(...)`.
  // Filter by allowedQuestionIds for matrix per-brand filtering.
  // codegen models `results` as nullable; an absent list means no per-question
  // tabulation, yielding an empty (header-only) table — not a bug, so flatten
  // a missing outer list as empty rather than throwing.
  const flatCountAnswers: CountAnswer[] = (result.results ?? [])
    .flatMap(R.identity())
    .filter(exists)
  const countAnswers: CountAnswer[] = allowedQuestionIds
    ? flatCountAnswers.filter((ca) =>
        allowedQuestionIds.includes(ca.questionId),
      )
    : flatCountAnswers

  const getBrandNineSegmentIndex = buildBrandNineSegmentIndexResolver(
    selectedBrandsInOrder,
  )

  // Engine sends groupCounts when it sends groupAnswers; if absent (older
  // payloads / unexpected shape), fall back to NaN-marker indexing so the
  // pure function's existing NaN behavior surfaces a missing-data bug instead
  // of inventing numbers.
  const groupCounts = result.groupCounts ?? []

  const segments = buildStandardAnalysisSegmentContext({
    brandId,
    getBrandNineSegmentIndex,
    groupCounts,
    sampleSize,
  })

  const nineSegAnswersArrayData = buildNineSegmentsAnswersArrayData({
    brand: segments.brand,
    countAnswers,
    getBrandNineSegmentIndex,
    getNineSegmentsData: segments.getNineSegmentsData,
    questions: questions as Question[],
    // Identity: no UI-driven sort in offline render.
    sort: (rows) => [...rows],
  })
  const fiveSegAnswersArrayData = buildFiveSegmentsAnswersArrayData({
    getFiveSegmentsData: segments.getFiveSegmentsData,
    nineSegmentsAnswersArrayData: nineSegAnswersArrayData,
    sort: (rows) => [...rows],
  })

  return assembleStandardAnalysisTableElements({
    fiveSeg: {
      answersArrayData: fiveSegAnswersArrayData,
      customerAttributesArrayData: [],
      totalData: segments.fiveSegTotalData,
    },
    nineSeg: {
      answersArrayData: nineSegAnswersArrayData,
      customerAttributesArrayData: [],
      totalData: segments.nineSegTotalData,
    },
    sampleSize,
    shouldAttachDriverBlock,
    t,
  })
}

// Same table shape as a question slot, but the rows are customer attributes instead of a
// question's answer options. `groupCounts` cannot come from this axis' own tabulation — the
// caller synthesises it (see buildGroupCountsFromCustomerAnalysis in buildTaskElements).
export const buildCustomerAttributeAxisTableElements = ({
  brandId,
  brandNumber,
  customerAttributeGroups,
  customerAttributeResults,
  groupCounts,
  sampleSize,
  selectedBrandsInOrder,
  t,
}: {
  brandId: number
  // NOT brandId: the engine response keys its per-brand blocks by brandNumber
  brandNumber: number
  customerAttributeGroups: {
    customerAttributes: { id: string; name: string }[]
    name: string
  }[]
  customerAttributeResults: CustomerAttributeAxisResult
  groupCounts: number[]
  sampleSize: number
  selectedBrandsInOrder: { id: number }[]
  t: TFunction
}): TableElement[] => {
  const segments = buildStandardAnalysisSegmentContext({
    brandId,
    getBrandNineSegmentIndex: buildBrandNineSegmentIndexResolver(
      selectedBrandsInOrder,
    ),
    groupCounts,
    sampleSize,
  })

  const nineSegCustomerAttributesArrayData =
    buildNineSegmentsCustomerAttributesArrayData({
      brandNumber,
      customerAttributeGroups,
      customerAttributeResults: [...customerAttributeResults],
      getNineSegmentsData: segments.getNineSegmentsData,
      // Identity: no UI-driven sort in offline render.
      sort: (rows) => [...rows],
    })
  const fiveSegCustomerAttributesArrayData =
    buildFiveSegmentsCustomerAttributesArrayData({
      getFiveSegmentsData: segments.getFiveSegmentsData,
      nineSegmentsCustomerAttributesArrayData:
        nineSegCustomerAttributesArrayData,
      sort: (rows) => [...rows],
    })

  return assembleStandardAnalysisTableElements({
    fiveSeg: {
      answersArrayData: [],
      customerAttributesArrayData: fiveSegCustomerAttributesArrayData,
      totalData: segments.fiveSegTotalData,
    },
    nineSeg: {
      answersArrayData: [],
      customerAttributesArrayData: nineSegCustomerAttributesArrayData,
      totalData: segments.nineSegTotalData,
    },
    sampleSize,
    // 行が顧客属性のみ (answersArrayData=[]) なのでブロックは元々付かないが、意図を明示する。
    shouldAttachDriverBlock: () => false,
    t,
  })
}

// Per-(brand,segment) totals plus the two curried row builders both standard-analysis axes
// need. Split out because the row builders depend on the totals, so the totals must exist
// before either axis can build its rows.
const buildStandardAnalysisSegmentContext = ({
  brandId,
  getBrandNineSegmentIndex,
  groupCounts,
  sampleSize,
}: {
  brandId: number
  getBrandNineSegmentIndex: (brand: Brand, nineSegment: NineSegment) => number
  groupCounts: number[]
  sampleSize: number
}) => {
  // brand is only read as `brand.id` (via getBrandNineSegmentIndex). The pure
  // function takes Brand for hook-parity; basic-output stubs the rest.
  const brand = { id: brandId } as Brand

  const nineSegTotalData = buildNineSegmentsTotalData({
    brand,
    getBrandNineSegmentIndex,
    groupCounts,
    sampleSize,
  })
  const fiveSegTotalData = buildFiveSegmentsTotalData({
    nineSegmentsTotalData: nineSegTotalData,
  })

  const getFiveSegmentsData: Parameters<
    typeof buildFiveSegmentsAnswersArrayData
  >[0]['getFiveSegmentsData'] = (fiveSegmentCounts, total) =>
    buildFiveSegmentsData({
      fiveSegmentCounts,
      total,
      totalDataCounts: fiveSegTotalData.counts,
    })
  const getNineSegmentsData: Parameters<
    typeof buildNineSegmentsAnswersArrayData
  >[0]['getNineSegmentsData'] = (nineSegmentCounts, total) =>
    buildNineSegmentsData({
      nineSegmentCounts,
      total,
      totalDataCounts: nineSegTotalData.counts,
    })

  return {
    brand,
    fiveSegTotalData,
    getFiveSegmentsData,
    getNineSegmentsData,
    nineSegTotalData,
  }
}

// Shared tail of both standard-analysis axes: only WHICH rows differ, so each axis passes its
// own rows and an empty array for the axis it does not use.
const assembleStandardAnalysisTableElements = ({
  fiveSeg,
  nineSeg,
  sampleSize,
  shouldAttachDriverBlock,
  t,
}: {
  fiveSeg: {
    answersArrayData: Parameters<
      typeof buildStdFiveSegmentsTableInfo
    >[0]['answersArrayData']
    customerAttributesArrayData: Parameters<
      typeof buildStdFiveSegmentsTableInfo
    >[0]['customerAttributesArrayData']
    totalData: Parameters<typeof buildStdFiveSegmentsTableInfo>[0]['totalData']
  }
  nineSeg: {
    answersArrayData: Parameters<
      typeof buildStdNineSegmentsTableInfo
    >[0]['answersArrayData']
    customerAttributesArrayData: Parameters<
      typeof buildStdNineSegmentsTableInfo
    >[0]['customerAttributesArrayData']
    totalData: Parameters<typeof buildStdNineSegmentsTableInfo>[0]['totalData']
  }
  sampleSize: number
  // ドライバーTop5ブロックを付ける設問のゲート。イメージ設問でない設問にブロックを付けると
  // LLM がドライバー分析へ誤誘導される (appendDriverTop5Block.ts 参照) ため、呼び出し元が
  // 役割 (npiDriver=常に真) や設問文 (isImageQuestionText) で判断して渡す。
  shouldAttachDriverBlock: (questionText: string) => boolean
  t: TFunction
}): TableElement[] => {
  const nineSegTotalData = nineSeg.totalData
  const fiveSegTotalData = fiveSeg.totalData

  const nineSegColumns = buildStdNineSegmentsTableColumns({
    hiddenColumnKeys: [],
  })
  const nineSegTableInfo = buildStdNineSegmentsTableInfo({
    addHighlightProps: () => ({}),
    answersArrayData: nineSeg.answersArrayData,
    customerAttributesArrayData: nineSeg.customerAttributesArrayData,
    dataDisplayType: 'percentage',
    hiddenColumnKeys: [],
    hiddenRowIds: [],
    sampleSize,
    sortValue: null,
    t,
    tamRatio: 1,
    totalData: nineSegTotalData,
  })

  const fiveSegColumns = buildStdFiveSegmentsTableColumns({
    hiddenColumnKeys: [],
  })
  const fiveSegTableInfo = buildStdFiveSegmentsTableInfo({
    addHighlightProps: () => ({}),
    answersArrayData: fiveSeg.answersArrayData,
    customerAttributesArrayData: fiveSeg.customerAttributesArrayData,
    dataDisplayType: 'percentage',
    hiddenRowIds: [],
    sampleSize,
    sortValue: null,
    t,
    tamRatio: 1,
    totalData: fiveSegTotalData,
  })

  return [
    {
      element: (
        <div style={{ backgroundColor: 'white', padding: 16 }}>
          <Table columns={fiveSegColumns} tableInfo={fiveSegTableInfo} />
        </div>
      ),
      segmentMode: '5segs',
      // Append the per-segment sample-size threshold block so the analyzer can
      // judge sample size per item, then the precomputed driver-Top5 block so the
      // analyzer never re-selects Top5 from the table. Table text stays fully
      // intact (append-only).
      // セグメント別%Top5ブロックは全設問に付ける (場合分けAの選択・読み飛ばし対策)。
      textContent: appendFiveSegSegmentTop5Block(
        appendFiveSegDriverTop5Block(
          appendSampleThresholdBlock(
            serializeTableToText(fiveSegColumns, fiveSegTableInfo),
            fiveSegmentThresholdInputs(fiveSegTotalData.counts, t),
          ),
          fiveSeg.answersArrayData.filter(
            (rows) =>
              0 < rows.length &&
              shouldAttachDriverBlock(rows[0].question.question_text),
          ),
          t,
        ),
        fiveSeg.answersArrayData,
        t,
        // ウィザードに列・行の非表示は無い。顧客属性の行 (顧客属性軸のスロット) も
        // 属性グループごとの節として載せる。
        [],
        fiveSeg.customerAttributesArrayData,
      ),
    },
    {
      element: (
        <div style={{ backgroundColor: 'white', padding: 16 }}>
          <Table columns={nineSegColumns} tableInfo={nineSegTableInfo} />
        </div>
      ),
      segmentMode: '9segs',
      textContent: appendNineSegSegmentTop5Block(
        appendNineSegDriverTop5Block(
          appendSampleThresholdBlock(
            serializeTableToText(nineSegColumns, nineSegTableInfo),
            nineSegmentThresholdInputs(nineSegTotalData.counts, t),
          ),
          nineSeg.answersArrayData.filter(
            (rows) =>
              0 < rows.length &&
              shouldAttachDriverBlock(rows[0].question.question_text),
          ),
          t,
        ),
        nineSeg.answersArrayData,
        t,
        [],
        nineSeg.customerAttributesArrayData,
      ),
    },
  ]
}
